import {
  FC,
  SyntheticEvent,
  useState,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useFormContext, Controller } from "react-hook-form";
import { TextField, Autocomplete, CircularProgress } from "@mui/material";
import { debounce } from "lodash";
import __ from "src/utils/Translations";
import useFetchAreasData from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchAreasData";
import { useParams } from "react-router-dom";

interface IAutocompleteAdditionalArea {
  companyId: string;
  structureId: string;
}

const AutocompleteAdditionalArea: FC<IAutocompleteAdditionalArea> = ({
  companyId,
  structureId,
}) => {
  const { control } = useFormContext();
  const [search, setSearch] = useState("");

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useFetchAreasData(companyId, structureId, search);

  const { areaId: excludedValue } = useParams<{ areaId: string }>();

  const options = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.content) || []).filter(
        (option) => option.id !== excludedValue
      ),
    [data?.pages, excludedValue]
  );

  const debouncedSearch = useRef(
    debounce((_: SyntheticEvent, value: string) => {
      setSearch(value);
    }, 300)
  ).current;

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleScroll = (event: SyntheticEvent) => {
    const listbox = event.currentTarget;
    if (
      (listbox.scrollTop + listbox.clientHeight) / listbox.scrollHeight > 0.9 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  return (
    <Controller
      name="additionalDisplayArea"
      control={control}
      render={({ field: { onChange, value }, fieldState }) => (
        <Autocomplete
          options={options}
          disableListWrap
          disablePortal
          value={value || null}
          onChange={(_, newValue) => onChange(newValue)}
          onInputChange={(event, newInputValue, reason) => {
            if (reason !== "reset") {
              debouncedSearch(event, newInputValue);
            }
          }}
          onOpen={() => setSearch("")}
          filterOptions={(options) => options}
          noOptionsText={__("Brak wyników wyszukiwania")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={__("Wyświetlany dodatkowy obszar")}
              error={fieldState.invalid}
              helperText={
                fieldState.error?.message ??
                __(
                  "Wyświetlany dodatkowy obszar będzie pokazany na karcie pracownika jako miejsce jego przynależności w strukturze organizacji obok obszaru, do którego należy. Przykład: Zespół kadr, Kadry i płace."
                )
              }
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isFetching && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          slotProps={{
            listbox: {
              onScroll: handleScroll,
              style: { maxHeight: "300px", overflowY: "auto" },
            },
          }}
        />
      )}
    />
  );
};

export default AutocompleteAdditionalArea;
